import type { RcFile } from 'antd/es/upload';
import commonApi from 'apis/common';

/**
 * 将文件转换为 Base64 字符串
 * @param file 文件对象
 * @returns Base64 字符串
 */
export const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

/**
 * 将多张图片合成为一张图片
 * @param files 图片文件数组
 * @returns 合成后的图片 Blob
 */
export const mergeImages = async (files: Blob[]): Promise<Blob> => {
  if (files.length === 0) {
    throw new Error('没有图片需要合成');
  }
  if (files.length === 1) {
    return files[0];
  }

  // 加载所有图片
  const images = await Promise.all(
    files.map(
      (file) =>
        new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          const url = URL.createObjectURL(file);
          img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('图片加载失败'));
          };
          img.src = url;
        }),
    ),
  );

  // 计算合成后的画布尺寸
  const maxWidth = Math.max(...images.map((img) => img.width));
  const totalHeight = images.reduce((sum, img) => sum + img.height, 0);
  const padding = 10; // 图片之间的间距
  const canvasHeight = totalHeight + padding * (images.length - 1);

  // 创建画布并绘制
  const canvas = document.createElement('canvas');
  canvas.width = maxWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('无法创建画布上下文');
  }

  // 填充白色背景（JPEG不支持透明度，需要白色背景）
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, maxWidth, canvasHeight);

  // 绘制所有图片（垂直排列，居中对齐）
  let currentY = 0;
  images.forEach((img) => {
    const x = (maxWidth - img.width) / 2; // 居中
    ctx.drawImage(img, x, currentY, img.width, img.height);
    currentY += img.height + padding;
  });

  // 将画布转换为 Blob（使用JPEG格式，文件更小，上传更快）
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('图片合成失败'));
        }
      },
      'image/jpeg',
      0.9,
    );
  });
};

/**
 * 逐个上传图片文件，返回以逗号拼接的 key 和签名 url
 * @param files 图片文件数组
 * @param recycleOrderId 有值时写入 images/recycle_order/{id}/
 */
export const uploadImageFiles = async (
  files: File[],
  recycleOrderId?: number | string,
): Promise<{ keys: string; urls: string }> => {
  const uploadedList = await Promise.all(
    files.map(async (file) => {
      const uploaded = await commonApi.uploadFile(file, true, {
        compress: true,
        recycleOrderId,
      });
      const signed = await commonApi.getSignedFileUrl(uploaded.key);
      return { key: uploaded.key, url: signed.url || uploaded.url };
    }),
  );

  return {
    keys: uploadedList.map((item) => item.key).join(','),
    urls: uploadedList.map((item) => item.url).join(','),
  };
};
